/* eslint-disable @typescript-eslint/naming-convention */
import { z } from 'zod';
import { addressSchema } from '../common/addressSchema';

/**
 * Schema for detailed spouse information (used when hasDetailedMarriageFields is true)
 *
 * The spouseInfo object itself is optional at parent schema level (so non-WI/MO states
 * can omit it entirely), but if the object exists, isMarried is required.
 *
 * Validation approach:
 * - UI layer: SpouseFormFields only renders for WI/MO states, RadioGroup has required attribute
 * - Schema layer: When spouseInfo object exists, validates isMarried and spouse details
 * - This is simpler than state-based validation at parent schema level
 */
export const spouseInfoSchema = z
    .object({
        isMarried: z.boolean({
            required_error: 'Select a marital status',
            invalid_type_error: 'Select a marital status',
        }),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        isAddressSameAsApplicant: z.boolean().optional(),
        address: addressSchema.optional(),
    })
    .superRefine((data, ctx) => {
        // If married, require spouse details
        if (data.isMarried === true) {
            if (!data.firstName || String(data.firstName).trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Enter your spouse first name',
                    path: ['firstName'],
                });
            }
            if (!data.lastName || String(data.lastName).trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Enter your spouse last name',
                    path: ['lastName'],
                });
            }
            if (!data.isAddressSameAsApplicant && data.address === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Spouse address is required when married',
                    path: ['address'],
                });
            }
        }
    });

const phoneRegex = /^((\d{3}\-|\(\d{3}\)\s)\d{3}-\d{4}|\d{10})$/;

/**
 * Validation function for simple marriage fields (used when hasDetailedMarriageFields is false)
 * Required for providers like ServiceFinance
 *
 * When married, validates:
 * - spouseFirstName and spouseLastName are present and non-empty
 * - spouseContact is either a valid email or phone number (10 digits with optional formatting)
 *
 * @param data - Object with isMarried, spouseFirstName, spouseLastName, and spouseContact fields
 * @param ctx - Zod refinement context
 * @param pathPrefix - Optional path prefix for error messages (e.g., ['coApplicant'])
 */
export const validateSimpleMarriageFields = (
    data: {
        isMarried?: boolean;
        spouseFirstName?: string;
        spouseLastName?: string;
        spouseContact?: string;
    },
    ctx: z.RefinementCtx,
    pathPrefix: string[] = [],
) => {
    if (data.isMarried === true) {
        if (!data.spouseFirstName || String(data.spouseFirstName).trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Enter your spouse first name',
                path: [...pathPrefix, 'spouseFirstName'],
            });
        }

        if (!data.spouseLastName || String(data.spouseLastName).trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Enter your spouse last name',
                path: [...pathPrefix, 'spouseLastName'],
            });
        }

        const spouseContact = data.spouseContact?.trim();

        if (!spouseContact) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Enter your spouse's email address or phone number",
                path: [...pathPrefix, 'spouseContact'],
            });
            return;
        }

        const isValidEmail = z.string().email().safeParse(spouseContact).success;
        const isValidPhone = phoneRegex.test(spouseContact);

        if (!isValidEmail && !isValidPhone) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Enter a valid email address or phone number',
                path: [...pathPrefix, 'spouseContact'],
            });
        }
    }
};
